import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePopupStore from "@/hooks/usePopupStore";
import InputGroups from "@/UI/components/InputGroups";
import MainMap, { type MapPoint } from "@/UI/components/MainMap";
import ConfirmPopup, { type ConfirmPopupDetails } from "@/UI/popups/ConfirmPopup";
import GoogleMapsUrlPopup, { type GoogleMapsUrlPopupDetails } from "@/UI/popups/GoogleMapsUrlPopup";
import { TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

// ── Types ─────────────────────────────────────────────────────────────────────

interface LangValue {
    lang: string;
    value: string;
    default?: boolean;
}

interface InitialBranchData {
    _id?: string;
    name: LangValue[];
    address: LangValue[];
    location?: { lat: number; lng: number } | { type?: string; coordinates?: [number, number] } | null;
}

export interface BranchesFormDetails {
    branchId?: string;
    initialData?: InitialBranchData;
    isDuplicate?: boolean;
    onSuccess: () => void;
}

interface FormState {
    nameAr: string;
    nameEn: string;
    addressAr: string;
    addressEn: string;
    location: { lat: number; lng: number } | null;
}

// ── MUI Shared Styles ─────────────────────────────────────────────────────────

const getTextFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    "& .MuiInputLabel-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-gray-02)",
        transformOrigin: dir === "rtl" ? "top right" : "top left",
        right: dir === "rtl" ? 0 : "unset",
        left: dir === "rtl" ? "unset" : 0,
        "&:not(.MuiInputLabel-shrink)": {
            transform: dir === "rtl" ? "translate(-14px, 14px) scale(1)" : "translate(14px, 14px) scale(1)",
        },
        "&.MuiInputLabel-shrink": {
            transform: dir === "rtl" ? "translate(-14px, -9px) scale(0.75)" : "translate(14px, -9px) scale(0.75)",
        },
        "&.Mui-focused": {
            color: "var(--color-orange)",
        },
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-lev3)",
        "& fieldset": {
            borderColor: "var(--color-gray-01)",
            borderWidth: "1.5px",
        },
        "& fieldset legend": {
            textAlign: dir === "rtl" ? "right" : "left",
            marginLeft: dir === "rtl" ? "auto" : undefined,
            marginRight: dir === "rtl" ? "0px" : undefined,
        },
        "&:hover fieldset": {
            borderColor: "var(--color-gray-02)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "var(--color-orange)",
            borderWidth: "1.5px",
        },
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px",
        textAlign: dir === "rtl" ? "right" : "left",
    },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function getLangValue(arr: LangValue[] | undefined, lang: string): string {
    if (!arr || !Array.isArray(arr)) return "";
    const found = arr.find((v) => v.lang === lang);
    return found?.value || "";
}

// Backend returns location as GeoJSON { type: "Point", coordinates: [lng, lat] }
// We convert it to { lat, lng } for the form
interface GeoJSONLocation {
    type?: string;
    coordinates?: [number, number];
}

function parseLocation(loc: { lat: number; lng: number } | GeoJSONLocation | null | undefined): { lat: number; lng: number } | null {
    if (!loc) return null;

    // Already in { lat, lng } format
    if ("lat" in loc && "lng" in loc) {
        return { lat: loc.lat, lng: loc.lng };
    }

    // GeoJSON format { type: "Point", coordinates: [lng, lat] }
    if ("coordinates" in loc && Array.isArray(loc.coordinates) && loc.coordinates.length >= 2) {
        const [lng, lat] = loc.coordinates;
        if (lng !== 0 || lat !== 0) {
            return { lat, lng };
        }
    }

    return null;
}

function buildInitialForm(initial: InitialBranchData | undefined): FormState {
    if (!initial) {
        return {
            nameAr: "",
            nameEn: "",
            addressAr: "",
            addressEn: "",
            location: null,
        };
    }

    return {
        nameAr: getLangValue(initial.name, "ar"),
        nameEn: getLangValue(initial.name, "en"),
        addressAr: getLangValue(initial.address, "ar"),
        addressEn: getLangValue(initial.address, "en"),
        location: parseLocation(initial.location),
    };
}

function buildRequestData(form: FormState) {
    const name: LangValue[] = [];
    if (form.nameAr.trim()) name.push({ lang: "ar", value: form.nameAr.trim(), default: true });
    if (form.nameEn.trim()) name.push({ lang: "en", value: form.nameEn.trim() });

    const address: LangValue[] = [];
    if (form.addressAr.trim()) address.push({ lang: "ar", value: form.addressAr.trim(), default: true });
    if (form.addressEn.trim()) address.push({ lang: "en", value: form.addressEn.trim() });

    return {
        name,
        address,
        location: form.location || undefined,
    };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
    onClose: () => void;
    details?: BranchesFormDetails;
}

export default function BranchesForm({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();

    const branchId = details?.branchId;
    const isDuplicate = details?.isDuplicate;
    const isEdit = !!branchId && !isDuplicate;

    const [form, setForm] = useState<FormState>(() => buildInitialForm(details?.initialData));

    // ── Helpers ───────────────────────────────────────────────────────────────

    const showError = (message: string) => {
        const name = "branch-form-error";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message,
                variant: "danger",
                confirmLabel: t.branchForm.ok,
                onConfirm: () => {},
            } as ConfirmPopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    // ── Geolocation center ────────────────────────────────────────────────────

    const [geoCenter, setGeoCenter] = useState<{ lat: number; lng: number } | null>(null);

    useEffect(() => {
        if (form.location) return;
        navigator.geolocation?.getCurrentPosition(
            (pos) => setGeoCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => {} // silently fall back to default center
        );
    }, []);

    // ── Location ──────────────────────────────────────────────────────────────

    const handleLocationChange = (markers: MapPoint[]) => {
        if (markers.length > 0) {
            setField("location", { lat: markers[0].lat, lng: markers[0].lng });
        } else {
            setField("location", null);
        }
    };

    const clearLocation = () => {
        setField("location", null);
    };

    const openGoogleMapsPopup = () => {
        const name = "google-maps-url";
        removeComp(name);
        addComp({
            name,
            Component: GoogleMapsUrlPopup,
            details: {
                onSuccess: (loc) => setField("location", loc),
            } as GoogleMapsUrlPopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    const mapPoints: MapPoint[] = form.location ? [{ lat: form.location.lat, lng: form.location.lng, color: "orange" }] : [];

    // ── Submit fetch ──────────────────────────────────────────────────────────

    const { loading: submitting, error: submitError, status: submitStatus, invoke: submitInvoke } = useFetch("branches", {}, false);

    const wasSubmitting = useRef(false);
    useEffect(() => {
        if (submitting) {
            wasSubmitting.current = true;
        } else if (wasSubmitting.current) {
            wasSubmitting.current = false;
            if (submitStatus === 201 || submitStatus === 200) {
                details?.onSuccess?.();
                onClose();
            } else if (submitError) {
                showError(submitError);
            }
        }
    }, [submitting]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!form.nameAr.trim() && !form.nameEn.trim()) {
            showError(t.branchForm.nameRequired);
            return;
        }

        const data = buildRequestData(form);

        submitInvoke({
            method: isEdit ? "PUT" : "POST",
            ...(isEdit ? { param: branchId } : {}),
            data,
        });
    };

    // ── Derived ────────────────────────────────────────────────────────────────

    const textFieldSxRtl = getTextFieldSx("rtl");
    const textFieldSxLtr = getTextFieldSx("ltr");

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <form className="branches-form" onSubmit={handleSubmit} noValidate>
            <div className="branches-form-fields">
                {/* ── Basic Info Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.branchForm.basicInfo}>
                    <div className="bf-row bf-row--2">
                        <TextField
                            fullWidth
                            label={`${t.branchForm.name} (${t.branchForm.arabic})`}
                            value={form.nameAr}
                            onChange={(e) => setField("nameAr", e.target.value)}
                            sx={textFieldSxRtl}
                        />
                        <TextField
                            fullWidth
                            label={`${t.branchForm.name} (${t.branchForm.english})`}
                            value={form.nameEn}
                            onChange={(e) => setField("nameEn", e.target.value)}
                            sx={textFieldSxLtr}
                        />
                    </div>
                    <div className="bf-row bf-row--2">
                        <TextField
                            fullWidth
                            label={`${t.branchForm.address} (${t.branchForm.arabic})`}
                            value={form.addressAr}
                            onChange={(e) => setField("addressAr", e.target.value)}
                            sx={textFieldSxRtl}
                        />
                        <TextField
                            fullWidth
                            label={`${t.branchForm.address} (${t.branchForm.english})`}
                            value={form.addressEn}
                            onChange={(e) => setField("addressEn", e.target.value)}
                            sx={textFieldSxLtr}
                        />
                    </div>
                </InputGroups>

                {/* ── Location Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.branchForm.locationSection}>
                    <p className="bf-location-hint">{t.branchForm.locationHint}</p>
                    <MainMap
                        input={1}
                        points={mapPoints}
                        onChange={handleLocationChange}
                        height={250}
                        lat={form.location?.lat}
                        lng={form.location?.lng}
                        zoom={form.location ? 14 : 11}
                        currentLocation={geoCenter ?? undefined}
                    />
                    <button type="button" className="bf-maps-url-btn" onClick={openGoogleMapsPopup}>
                        <img src="/icons/location-icon.svg" alt="" width={14} height={14} />
                        <span>{t.branchForm.addGoogleMapsUrl}</span>
                    </button>
                    {form.location && (
                        <div className="bf-location-coords">
                            <span className="bf-location-coords-text">
                                {form.location.lat.toFixed(6)}, {form.location.lng.toFixed(6)}
                            </span>
                            <button type="button" className="bf-location-clear" onClick={clearLocation}>
                                <img src="/icons/delete-icon.svg" alt="" width={14} height={14} />
                            </button>
                        </div>
                    )}
                </InputGroups>
            </div>

            {/* ── Footer ── */}
            <div className="bf-footer">
                <button type="button" className="bf-btn bf-btn--cancel" onClick={onClose} disabled={submitting}>
                    {t.branchForm.cancel}
                </button>
                <button type="submit" className="bf-btn bf-btn--submit" disabled={submitting}>
                    {submitting ? <span className="bf-spinner" /> : isEdit ? t.branchForm.update : t.branchForm.create}
                </button>
            </div>
        </form>
    );
}
